/**
 * Data Quality checks available on the explorer column table.
 * Mirrors the 8 active checks in the legacy Angular app
 * (`commonConstants.ts` `DataQuality.dataQuality()`).
 *
 * For each check the backend `dq_check` field is populated by joining
 * each column's `formatted` values with `, `.
 *   - Simple checks contribute a fixed token (e.g. `none`, `blank`).
 *   - Modal-driven checks contribute a `prefix?value` token after the
 *     user fills in the modal (e.g. `range?1/100`, `isin?a,b,c`).
 *
 * If the user opens the modal for a check and cancels, that check is
 * not added to the column.
 */

export type DqCheckName =
    | 'Null Check'
    | 'Redundant Check'
    | 'Range Check'
    | 'Isin Set'
    | 'Length Check'
    | 'Format Check'
    | 'Blank Check'
    | 'No Special Characters Check';

export type DqCheckModalKind = 'range' | 'value';

export interface DqCheckDefinition {
    name: DqCheckName;
    /** Token used in the comma-joined `dq_check` payload field for simple checks. */
    simpleValue?: string;
    /** Prefix used in `prefix?<input>` token for modal-driven checks. */
    modalPrefix?: string;
    /** Which input shape the modal uses; absent for simple checks. */
    modal?: DqCheckModalKind;
}

export const DQ_CHECKS: DqCheckDefinition[] = [
    { name: 'Null Check', simpleValue: 'none' },
    { name: 'Redundant Check', simpleValue: 'duplicate' },
    { name: 'Range Check', modalPrefix: 'range', modal: 'range' },
    { name: 'Isin Set', modalPrefix: 'isin', modal: 'value' },
    { name: 'Length Check', modalPrefix: 'length', modal: 'value' },
    { name: 'Format Check', modalPrefix: 'pattern', modal: 'value' },
    { name: 'Blank Check', simpleValue: 'blank' },
    { name: 'No Special Characters Check', simpleValue: 'no_special_chars' },
];

export const DQ_CHECK_BY_NAME: Record<DqCheckName, DqCheckDefinition> = DQ_CHECKS.reduce(
    (acc, def) => {
        acc[def.name] = def;
        return acc;
    },
    {} as Record<DqCheckName, DqCheckDefinition>,
);

export function isDqCheckName(value: string): value is DqCheckName {
    return Object.prototype.hasOwnProperty.call(DQ_CHECK_BY_NAME, value);
}

/** A confirmed DQ check on a column — its display name + the formatted token sent to the backend. */
export interface DqCheckEntry {
    name: DqCheckName;
    value: string;
}

/** Builds the formatted token for a simple (non-modal) check. Returns null for modal checks. */
export function buildSimpleDqValue(name: DqCheckName): string | null {
    const def = DQ_CHECK_BY_NAME[name];
    return def.simpleValue ?? null;
}

/** Builds `range?{min}/{max}` for the Range Check modal. */
export function buildRangeDqValue(min: string, max: string): string {
    return `range?${min.trim()}/${max.trim()}`;
}

/** Builds `prefix?{value}` for Isin Set / Length Check / Format Check modals. */
export function buildValueDqValue(name: DqCheckName, value: string): string {
    const def = DQ_CHECK_BY_NAME[name];
    if (!def.modalPrefix) {
        throw new Error(`buildValueDqValue called for non-modal check ${name}`);
    }
    return `${def.modalPrefix}?${value.trim()}`;
}
